import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { ContextMenu, PanelBackButton, Tip } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import { MessageContent, ReactionChips, UnsentNote, quickReactionEmojis, ForwardModal, DeleteMessageConfirm } from './MessageList'
import MessageInput from './MessageInput'
import AuditBadge from './AuditBadge'
import ReactionPicker from './ReactionPicker'
import UserAvatar from './UserAvatar'
import { formatTime } from '../utils/time'
import { getFileUrl } from '../services/api'
import { CornerUpRight } from 'react-feather'
import { MailOpen, Pin } from 'lucide-react'

function ThreadFileCard({ file, onImageClick }) {
  const isImage = file.mimetype?.startsWith('image/')
  const sizeStr = file.size < 1024 ? `${file.size} B`
    : file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB`
    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`

  if (isImage) {
    return (
      <div className="file-card">
        <img
          src={getFileUrl(file.id)}
          alt={file.original_filename}
          className="file-card-image"
          onClick={() => onImageClick?.(getFileUrl(file.id), file.original_filename)}
          style={{ cursor: 'pointer' }}
        />
      </div>
    )
  }

  return (
    <div className="file-card">
      <div className="file-card-body">
        <div className="file-card-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <div className="file-card-info">
          <span className="file-card-name">{file.original_filename}</span>
          <span className="file-card-size">{sizeStr}</span>
        </div>
        <Tip label="Download">
        <a href={getFileUrl(file.id)} className="file-card-download" download>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </a>
        </Tip>
      </div>
    </div>
  )
}

function ThreadMessage({ msg, onImageClick, pickerMsgId, setPickerMsgId, onReactionClick, onPickerSelect, currentUser, onForwardClick, resolveUsername, onPinMessage, pinnedMessageIds, onStartEdit, onDeleteMessage, onMarkUnread, onReply }) {
  const isOwn = msg.sender_id === currentUser?.id
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Right-click menu (shell <ContextMenu>) — reactions row + actions, declaratively.
  const reactionEntries = [
    ...quickReactionEmojis().map((emoji) => ({ content: emoji, onSelect: () => onReactionClick(msg.id, emoji, []) })),
    { className: 'ofself-menu-reaction--more', content: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>, onSelect: () => setPickerMsgId(msg.id) },
  ]
  const copyItem = { label: 'Copy Text', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>, onSelect: () => navigator.clipboard.writeText(msg.content) }
  // an unsent reply exists only here: nothing the server knows about can be done to it
  const menuItems = msg._unsent ? [copyItem] : [
    { label: 'Reply', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>, onSelect: () => onReply?.(msg) },
    copyItem,
    isOwn && { label: 'Edit', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>, onSelect: () => onStartEdit(msg) },
    { label: pinnedMessageIds?.has(msg.id) ? 'Unpin' : 'Pin', icon: <Pin size={14} />, onSelect: () => onPinMessage(msg.id) },
    { label: 'Forward', icon: <CornerUpRight size={14} />, onSelect: () => onForwardClick(msg) },
    { label: 'Mark as unread', icon: <MailOpen size={14} />, onSelect: () => onMarkUnread(msg) },
    isOwn && { label: 'Delete', danger: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>, onSelect: () => setConfirmDelete(true) },
  ]

  const rowEl = (
    <div className={`block__row message${msg._unsent ? ' message--unsent' : ''}`}>
      <UserAvatar
        sender={msg.sender}
        userID={msg.sender?.id || msg.user_id}
        initials={msg.sender_type === 'plugin' ? 'P' : (msg.sender?.initials || (msg.username || msg.agent_meta?.agent_name || '?')[0]?.toUpperCase() || '??')}
        className="message-avatar"
      />
      <div className="message-body">
        <div className="message-meta">
          <span className="message-sender">{msg.sender_type === 'plugin' ? (msg.agent_meta?.display_name || 'Plugin') : (msg.sender?.username || msg.username || msg.agent_meta?.agent_name || 'Unknown')}</span>
          {msg.sender_type === 'plugin' && <span style={{ fontSize: 'var(--ofself-text-sm)', padding: '1px 4px', borderRadius: 3, background: '#2d6a4f', color: '#fff', marginLeft: 4 }}>Plugin</span>}
          <span className="message-time">{formatTime(msg.timestamp)}</span>
          {msg.auditTrail && <AuditBadge />}
        </div>
        <div className="message-content">
          <MessageContent content={msg.content} />
          {msg.edited && <span className="message-edited-tag">(edited)</span>}
          <span className="bubble-time">{formatTime(msg.timestamp)}</span>
        </div>
        {msg._unsent && <UnsentNote msg={msg} />}
        {msg.file && <ThreadFileCard file={msg.file} onImageClick={onImageClick} />}
        <ReactionChips
          reactions={msg.reactions}
          currentUserId={currentUser?.id}
          resolveUsername={(id) => (resolveUsername ? resolveUsername(id) : id?.slice(0, 8))}
          onToggle={(emoji, userIds) => onReactionClick(msg.id, emoji, userIds)}
        />
        <ReactionPicker
          open={pickerMsgId === msg.id}
          onOpenChange={(o) => setPickerMsgId(o ? msg.id : null)}
          onSelect={(emoji) => onPickerSelect(msg.id, emoji)}
        />
      </div>
      <DeleteMessageConfirm msg={msg} open={confirmDelete} onCancel={() => setConfirmDelete(false)} onConfirm={() => { onDeleteMessage(msg.id); setConfirmDelete(false) }} />
    </div>
  )

  return <ContextMenu trigger={rowEl} reactions={msg._unsent ? [] : reactionEntries} items={menuItems} />
}

export default function ThreadView({ open, basis, separatorProps, isMobile }) {
  const {
    threadMessage, threadReplies, closeThread, sendThreadReply,
    addReaction, removeReaction, currentUser,
    forwardMessage, channels, dmChannels, activeChannel,
    pinMessage, unpinMessage, pinnedMessages,
    deleteMessage, setEditingMessage, markAsUnread,
    setReplyingToMessage
  } = useChat()
  const [lightbox, setLightbox] = useState(null)
  const [pickerMsgId, setPickerMsgId] = useState(null)
  const [forwardMsg, setForwardMsg] = useState(null)
  const panelRef = useRef(null)
  const messagesEndRef = useRef(null)

  // Scroll to bottom when thread opens or new replies arrive
  const prevThreadIdRef = useRef(null)
  useEffect(() => {
    if (!messagesEndRef.current) return
    const isNewThread = threadMessage?.id !== prevThreadIdRef.current
    prevThreadIdRef.current = threadMessage?.id
    // Scroll the thread's OWN body, NOT scrollIntoView. scrollIntoView walks every
    // scrollable ancestor — and while the thread is still easing in from width 0, the
    // anchor sits off the right edge, so the browser scrolls the STAGE (overflow:hidden)
    // hard left to reveal it, shoving the whole app sideways (sidebar clips, thread
    // gains a right gap). It sticks, and it's gone on refresh (already full-width = anchor
    // in view). Scrolling the panel body directly only ever moves the thread.
    const scroller = messagesEndRef.current.closest('.block__body')
    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: isNewThread ? 'instant' : 'smooth' })
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: isNewThread ? 'instant' : 'smooth', block: 'nearest' })
    }
  }, [threadMessage?.id, threadReplies.length])

  const handleThreadSend = useCallback((text, fileId) => {
    sendThreadReply(text, fileId)
  }, [sendThreadReply])

  // No early return: the <aside> is ALWAYS mounted (so the kit's flex-basis ease has
  // a stable element to transition both ways); only its CONTENT below depends on
  // there being a thread message. All hooks therefore run every render.
  const pinnedMessageIds = useMemo(() => new Set((pinnedMessages || []).map(p => p.message_id)), [pinnedMessages])

  function handleImageClick(src, alt) { setLightbox({ src, alt }) }

  function handleReactionClick(msgId, emoji, userIds) {
    if (userIds && userIds.includes(currentUser?.id)) {
      removeReaction(msgId, emoji)
    } else {
      addReaction(msgId, emoji)
    }
  }

  function handlePickerSelect(msgId, emoji) {
    addReaction(msgId, emoji)
    setPickerMsgId(null)
  }

  function handleForward(targetChannelId, message) {
    forwardMessage(targetChannelId, message)
    setForwardMsg(null)
  }

  function handlePinMessage(messageId) {
    if (pinnedMessageIds.has(messageId)) {
      unpinMessage(messageId)
    } else {
      pinMessage(messageId)
    }
  }

  function handleStartEdit(msg) {
    setEditingMessage(msg)
  }

  function handleDeleteMessage(messageId) {
    if (deleteMessage) deleteMessage(messageId)
  }

  function handleMarkUnread(msg) {
    if (activeChannel?.id && msg?.id) {
      markAsUnread(activeChannel.id, msg.id)
    }
  }

  function handleReply(msg) {
    setReplyingToMessage(msg)
  }

  // Build userId -> username map from thread messages
  const userNameMap = {}
  if (threadMessage?.sender?.id) userNameMap[threadMessage.sender.id] = threadMessage.sender.username
  for (const r of threadReplies) {
    if (r.sender?.id) userNameMap[r.sender.id] = r.sender.username
  }
  if (currentUser?.id) userNameMap[currentUser.id] = currentUser.username || 'You'

  function resolveUsername(userId) {
    return userNameMap[userId] || userId?.slice(0, 8) || '?'
  }

  return (
    // Panel N+1 — the thread, a first-class .block that's a flat sibling of the
    // chat panel. It opens/closes purely by its flex-basis (0 ↔ width), and the kit
    // eases that change; aria-hidden fades the body as it closes. The resize handle
    // sits on its left seam, like every other panel.
    <aside
      className="block thread"
      ref={panelRef}
      data-qh-title="Thread"
      data-qh-body="A focused reply chain for the selected message — discuss it in depth without interrupting the main conversation."
      /* open → flex-basis is the concrete resized width (px); closed → 0 to ease shut. */
      style={isMobile
        ? { flex: '0 0 100%', minWidth: 0 }
        // CONCRETE px basis: open → var(--thread-w) so live resizes reflect instantly;
        // closed → literal 0px so useResizablePanel's continuous --thread-w sync can't
        // hold the panel at a non-zero width while it's supposed to be shut. The CSS
        // transition still fires (from the previously-rendered width to 0), so the smooth
        // slide is preserved in both directions.
        : { flex: open ? `0 0 var(--thread-w, ${basis}px)` : '0 0 0px', minWidth: 0 }}
      aria-hidden={open ? undefined : true}
      // inert when closed: pulls focus out (the composer was holding it, which is
      // what made aria-hidden complain) and blocks interaction during the close.
      inert={open ? undefined : ''}
    >
      {open && separatorProps && <div className="block__resize" {...separatorProps} />}

      {/* Content renders whenever there's a thread message — it persists while the
          panel eases closed; the aside itself stays mounted regardless. */}
      {threadMessage && (<>
        <div className="thread-header block__title">
          <div className="block__title-surface">
          {/* Mobile: the boxy back cell (shared .ofself-panel-back) — closes the thread,
              which slides back to the conversation. Matches every other panel back. */}
          {isMobile && <PanelBackButton onClick={closeThread} aria-label="Back" />}
          <span className="block__title-name">
            Thread
          </span>
          {/* Desktop: the in-place collapse chevron (right). Hidden on mobile, where the
              back cell carries the dismiss. */}
          {!isMobile && (
            <button className="block__title-close" onClick={closeThread} aria-label="Close thread">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
          </div>
        </div>

      <div className="block__body thread-messages">
        <ThreadMessage
          msg={threadMessage}
          onImageClick={handleImageClick}
          pickerMsgId={pickerMsgId}
          setPickerMsgId={setPickerMsgId}
          onReactionClick={handleReactionClick}
          onPickerSelect={handlePickerSelect}
          currentUser={currentUser}
          onForwardClick={setForwardMsg}
          resolveUsername={resolveUsername}
          onPinMessage={handlePinMessage}
          pinnedMessageIds={pinnedMessageIds}
          onStartEdit={handleStartEdit}
          onDeleteMessage={handleDeleteMessage}
          onMarkUnread={handleMarkUnread}
          onReply={handleReply}
        />

        {threadReplies.length > 0 && (
          <>
            {/* divider only exists once there are replies — fades in with them */}
            <div className="thread-divider thread-divider--in">
              <span>{threadReplies.length} {threadReplies.length === 1 ? 'reply' : 'replies'}</span>
            </div>
            {threadReplies.map((reply) => (
              <ThreadMessage
                key={reply.id}
                msg={reply}
                onImageClick={handleImageClick}
                pickerMsgId={pickerMsgId}
                setPickerMsgId={setPickerMsgId}
                onReactionClick={handleReactionClick}
                onPickerSelect={handlePickerSelect}
                currentUser={currentUser}
                onForwardClick={setForwardMsg}
                resolveUsername={resolveUsername}
                onPinMessage={handlePinMessage}
                pinnedMessageIds={pinnedMessageIds}
                onStartEdit={handleStartEdit}
                onDeleteMessage={handleDeleteMessage}
                onMarkUnread={handleMarkUnread}
                onReply={handleReply}
              />
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="thread-input-wrap block__foot block__foot--flow">
        <MessageInput onSend={handleThreadSend} placeholder="Reply" />
      </div>

      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <img src={lightbox.src} alt={lightbox.alt} className="lightbox-image" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {forwardMsg && (
        <ForwardModal
          message={forwardMsg}
          channels={channels || []}
          dmChannels={dmChannels || []}
          onForward={handleForward}
          onClose={() => setForwardMsg(null)}
        />
      )}
      </>)}
    </aside>
  )
}
